// Runs FFmpeg's main() to completion, one module instance per run.

import type { EmscriptenFS, EmscriptenModule } from './module.js';

/** Callbacks a module instance must be created with. */
export interface InstanceHooks {
  /** Emscripten's Module.onExit: main() returned, with this exit code. */
  onExit(code: number): void;
  /** Emscripten's Module.onAbort: the runtime aborted instead of exiting. */
  onAbort(what: unknown): void;
}

/** Creates one module instance wired to `hooks`. */
export type Instantiate = (hooks: InstanceHooks) => Promise<EmscriptenModule>;

interface Instance {
  module: EmscriptenModule;
  /** Settles once main() has run to completion on this instance. */
  done: Promise<number>;
}

/** Trees the runtime creates for itself in every instance; never copied. */
const RUNTIME_DIRS = new Set(['/dev', '/proc']);

/**
 * Owns the module instances behind one FFmpeg session.
 *
 * The core is built with `-sPROXY_TO_PTHREAD=1` and `-sEXIT_RUNTIME=1`:
 *
 * - PROXY_TO_PTHREAD runs main() on a pthread, so the thread that owns the
 *   runtime stays free to start more pthread Workers on demand. Without it, a
 *   command needing more threads than the pre-spawned pool deadlocks (#1).
 *   It also means callMain() only *starts* main() and returns 0 at once; the
 *   real exit code arrives later through Module.onExit.
 * - FFmpeg's main() is not re-entrant: its globals are not reset between
 *   calls, so a second run on one instance sees the first run's state (#2).
 *   EXIT_RUNTIME also tears the runtime down when main() returns. So every run
 *   after the first gets a fresh instance, with the previous instance's files
 *   copied in first.
 *
 * An instance's MEMFS stays readable and writable after its runtime exits, so
 * the last instance to run keeps serving `fs` until the next run.
 */
export class CoreSession {
  private current!: Instance;
  private ran = false;

  private constructor(private readonly instantiate: Instantiate) {}

  static async create(instantiate: Instantiate): Promise<CoreSession> {
    const session = new CoreSession(instantiate);
    session.current = await session.spawn();
    return session;
  }

  /** The filesystem of the current instance. */
  get fs(): EmscriptenFS {
    return this.current.module.FS;
  }

  /** Runs main() with `args` and resolves with its exit code. */
  async run(args: string[]): Promise<number> {
    if (this.ran) {
      const next = await this.spawn();
      copyTree(this.current.module.FS, next.module.FS, '/');
      this.current = next;
    }
    this.ran = true;

    // Under Node the runtime's exit path sets process.exitCode to main()'s
    // status. That is the host's exit status, not ours to change.
    const host = (globalThis as { process?: { exitCode?: number | string } }).process;
    const hostExitCode = host?.exitCode;
    try {
      this.current.module.callMain(args); // only starts main(); see above
      return await this.current.done;
    } finally {
      if (host) host.exitCode = hostExitCode;
    }
  }

  private async spawn(): Promise<Instance> {
    let settle!: { resolve: (code: number) => void; reject: (err: Error) => void };
    const done = new Promise<number>((resolve, reject) => (settle = { resolve, reject }));
    // Created eagerly but only awaited by run(); keep an unobserved rejection
    // from surfacing as unhandled.
    done.catch(() => {});
    const module = await this.instantiate({
      onExit: (code) => settle.resolve(code),
      onAbort: (what) => settle.reject(new Error(`FFmpeg aborted: ${String(what)}`)),
    });
    return { module, done };
  }
}

/** Recursively copies `dir` from one instance's FS into another's. */
function copyTree(from: EmscriptenFS, to: EmscriptenFS, dir: string): void {
  for (const name of from.readdir(dir)) {
    if (name === '.' || name === '..') continue;
    const path = dir === '/' ? `/${name}` : `${dir}/${name}`;
    if (RUNTIME_DIRS.has(path)) continue;

    const { mode } = from.lstat(path);
    if (from.isDir(mode)) {
      if (!to.analyzePath(path).exists) to.mkdir(path);
      copyTree(from, to, path);
    } else if (from.isLink(mode)) {
      to.symlink(from.readlink(path), path);
    } else if (from.isFile(mode)) {
      // readFile returns a fresh copy, so the new instance can take ownership
      // of it instead of copying again.
      to.writeFile(path, from.readFile(path), { canOwn: true });
    }
  }
}
